/**
 * Lightweight ring-buffer breadcrumb store for the SPA.
 *
 * Two independent ring buffers (capacity 20 each) accumulate the most-recent
 * route changes and `fetch` round-trips so that when `trackException` fires
 * we can attach the run-up context (where the user was, what API calls just
 * landed) as a single serialised property on the AI envelope.
 *
 * Pure module — no React, no AI SDK, no DOM. Safe to import from any layer
 * (router effect, mutator response handler, AI helper).
 *
 * Snapshot semantics:
 *   - Oldest entry first within each buffer (chronological).
 *   - Read-only result; mutating it has no effect on the live buffers.
 *   - Calling `snapshot()` does not drain or reset state.
 *
 * Eviction: writing past capacity drops the oldest entry. Both buffers evict
 * independently so a fetch spike doesn't push route history out of scope.
 */

const CAPACITY = 20;

export interface RouteEntry {
  /** Path the router navigated to (`location.pathname`, no search/hash). */
  path: string;
  /** Epoch ms when `pushRoute` was called. */
  ts: number;
}

export interface FetchEntry {
  /** Request URL as observed by the mutator (full URL, no body). */
  url: string;
  /** HTTP status code on the response. Aborted/network-failed calls are not recorded. */
  status: number;
  /** Wall-clock duration in ms (rounded). */
  durationMs: number;
  /** Epoch ms when the response landed. */
  ts: number;
}

export interface BreadcrumbSnapshot {
  routes: RouteEntry[];
  fetches: FetchEntry[];
}

let routes: RouteEntry[] = [];
let fetches: FetchEntry[] = [];

export function pushRoute(path: string): void {
  routes.push({ path, ts: Date.now() });
  if (routes.length > CAPACITY) {
    routes.splice(0, routes.length - CAPACITY);
  }
}

export function pushFetch(url: string, status: number, durationMs: number): void {
  fetches.push({ url, status, durationMs: Math.round(durationMs), ts: Date.now() });
  if (fetches.length > CAPACITY) {
    fetches.splice(0, fetches.length - CAPACITY);
  }
}

/**
 * Returns a defensive copy of both buffers (oldest-first). The result is
 * detached from the live state — callers may freely serialise, truncate, or
 * discard it without affecting subsequent pushes.
 */
export function snapshot(): BreadcrumbSnapshot {
  return {
    routes: routes.slice(),
    fetches: fetches.slice(),
  };
}

/**
 * Test-only reset. Not exported through the public surface; consumed by the
 * spec file via direct import path.
 */
export function _resetBreadcrumbsForTests(): void {
  routes = [];
  fetches = [];
}
